import { useState, FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useI18n } from "@expertloop/i18n/react";
import { adminApi, AdminApiError } from "../services/api";

const MIN_PASSWORD_LENGTH = 8;

/**
 * Choix d'un nouveau mot de passe à partir du lien reçu par email. Le lien porte
 * son jeton dans l'adresse (`?token=`) ; il est à usage unique.
 */
export function ResetPasswordPage() {
	const { t } = useI18n();
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token") ?? "";

	const [password, setPassword] = useState("");
	const [confirmation, setConfirmation] = useState("");
	const [error, setError] = useState("");
	const [isSaving, setIsSaving] = useState(false);
	const [hasChanged, setHasChanged] = useState(false);
	const [isLinkInvalid, setIsLinkInvalid] = useState(false);

	const validationError = (): string => {
		if (password.length < MIN_PASSWORD_LENGTH) return t("adminPasswordReset.passwordTooShort");
		if (password !== confirmation) return t("adminPasswordReset.passwordMismatch");
		return "";
	};

	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault();
		const problem = validationError();
		if (problem) {
			setError(problem);
			return;
		}

		setError("");
		setIsSaving(true);

		try {
			await adminApi.post("/admin/auth/reset-password", { token, password });
			setHasChanged(true);
		} catch (err) {
			// 400 : lien inconnu, expiré ou déjà utilisé. Le réessayer ne sert à rien.
			if (err instanceof AdminApiError && err.status === 400) {
				setIsLinkInvalid(true);
			} else {
				setError(t("adminPasswordReset.requestFailed"));
			}
		} finally {
			setIsSaving(false);
		}
	};

	if (hasChanged) {
		return (
			<div className="login-page">
				<div className="login-card">
					<h1>{t("adminPasswordReset.resetSuccessTitle")}</h1>
					<p className="subtitle">{t("adminPasswordReset.resetSuccessBody")}</p>
					<Link to="/login" className="btn btn-primary">
						{t("adminPasswordReset.backToLogin")}
					</Link>
				</div>
			</div>
		);
	}

	if (!token || isLinkInvalid) {
		return (
			<div className="login-page">
				<div className="login-card">
					<h1>{t("adminPasswordReset.resetTitle")}</h1>
					<div className="error-message">
						{token ? t("adminPasswordReset.invalidLink") : t("adminPasswordReset.missingToken")}
					</div>
					<Link to="/forgot-password" className="btn btn-primary">
						{t("adminPasswordReset.requestNewLink")}
					</Link>
				</div>
			</div>
		);
	}

	return (
		<div className="login-page">
			<div className="login-card">
				<h1>{t("adminPasswordReset.resetTitle")}</h1>
				<p className="subtitle">{t("adminPasswordReset.resetIntro")}</p>

				{error && <div className="error-message">{error}</div>}

				<form onSubmit={handleSubmit}>
					<div className="form-group">
						<label htmlFor="password">{t("adminPasswordReset.newPassword")}</label>
						<input
							id="password"
							type="password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							autoComplete="new-password"
							required
							autoFocus
						/>
					</div>

					<div className="form-group">
						<label htmlFor="confirmation">{t("adminPasswordReset.confirmPassword")}</label>
						<input
							id="confirmation"
							type="password"
							value={confirmation}
							onChange={(e) => setConfirmation(e.target.value)}
							autoComplete="new-password"
							required
						/>
					</div>

					<button type="submit" className="btn btn-primary" disabled={isSaving}>
						{isSaving ? t("adminPasswordReset.resetting") : t("adminPasswordReset.resetSubmit")}
					</button>
				</form>
			</div>
		</div>
	);
}
